import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import HideBadgeOnMobile from './HideBadgeOnMobile';
import { AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: formData.username.trim().toLowerCase(),
        password: formData.password.trim()
      });
      toast.success('Welcome back!');
      onLogin(response.data.token, response.data.user);
    } catch (err) {
      let errorMsg = 'Something went wrong. Please try again.';
      if (err.response) {
        if (err.response.status === 401) {
          errorMsg = 'Incorrect username or password. Please try again.';
        } else if (err.response.data?.detail) {
          errorMsg = err.response.data.detail;
        }
      } else if (err.request) {
        errorMsg = 'Cannot connect to server. Check your internet connection.';
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1676655079738-af54dfd6318e?crop=entropy&cs=srgb&fm=jpg&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <HideBadgeOnMobile />
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      <Card className="w-full max-w-md relative z-10 bg-[#18181B]/95 border-[#27272A] backdrop-blur-xl" data-testid="login-card">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_wod-trainer/artifacts/rhui1jxy_R%26R%20ELITE_20250812_172035_0000.png"
              alt="R&R Elite Fitness"
              className="w-32 h-32 object-contain"
            />
          </div>
          <CardTitle className="text-4xl font-heading font-black text-center uppercase tracking-tighter text-[#FAFAFA]">
            R&R Elite Fitness
          </CardTitle>
          <CardDescription className="text-center text-[#A1A1AA] text-sm tracking-wide uppercase">
            Rise & Recover
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm p-3 flex items-start gap-2" data-testid="login-error">
                <AlertCircle className="w-5 h-5 text-[#EF4444] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#EF4444]">{error}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="username" className="text-[#FAFAFA] uppercase tracking-wide text-xs font-semibold">
                Username
              </Label>
              <Input
                id="username"
                data-testid="email-input"
                type="text"
                value={formData.username}
                onChange={(e) => { setFormData({ ...formData, username: e.target.value }); setError(''); }}
                required
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
                className="bg-[#09090B] border-[#27272A] text-white focus:border-[#CCFF00] focus:ring-[#CCFF00] h-12"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#FAFAFA] uppercase tracking-wide text-xs font-semibold">
                Password
              </Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={formData.password}
                onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setError(''); }}
                required
                className="bg-[#09090B] border-[#27272A] text-white focus:border-[#CCFF00] focus:ring-[#CCFF00] h-12"
              />
            </div>
            
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] shadow-[0_0_15px_rgba(204,255,0,0.2)] transition-all active:scale-95 rounded-sm h-12 text-base"
            >
              {loading ? 'Please wait...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
